class OpenEyesMobilePoster extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.getAttribute('section-id');
    this.isVideo = this.getAttribute('is-video') === 'true';
    this.initTimer = null;
    this.posterAnimationTimer = null;
    this.resizeTimer = null;
    this.timeline = null;
    this.textUnlockProgress = 0;
    this.isPosterAnimating = false;
    this.shouldLockOnCurrentEntry = true;
    this.isIOS = /iP(ad|hone|od)/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    this.lockedScrollY = 0;
    this.touchStartY = 0;
    this.lastUserScrollIntentAt = 0;
    this.onResize = this.onResize.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onWheel = this.onWheel.bind(this);
  }

  connectedCallback() {
    if (this.isHidden()) return;

    this.initTimer = window.setTimeout(() => {
      this.setupTimeline();
    }, 1000);

    window.addEventListener('resize', this.onResize, { passive: true });
    if (!this.isIOS) {
      window.addEventListener('touchstart', this.onTouchStart, { passive: true });
      window.addEventListener('touchmove', this.onTouchMove, { passive: false });
      window.addEventListener('wheel', this.onWheel, { passive: false });
    }
  }

  disconnectedCallback() {
    window.clearTimeout(this.initTimer);
    window.clearTimeout(this.posterAnimationTimer);
    window.clearTimeout(this.resizeTimer);
    window.removeEventListener('resize', this.onResize);
    if (!this.isIOS) {
      window.removeEventListener('touchstart', this.onTouchStart);
      window.removeEventListener('touchmove', this.onTouchMove);
      window.removeEventListener('wheel', this.onWheel);
    }
    this.destroyTimeline();
  }

  onTouchStart(event) {
    this.lastUserScrollIntentAt = Date.now();
    this.touchStartY = event.touches?.[0]?.clientY ?? 0;
  }

  onTouchMove(event) {
    const currentY = event.touches?.[0]?.clientY ?? 0;
    const deltaY = this.touchStartY - currentY;

    if (this.shouldBlockForwardScroll(deltaY)) {
      event.preventDefault();
      this.enforcePosterScrollLock();
      this.touchStartY = currentY;
    }
  }

  onWheel(event) {
    this.lastUserScrollIntentAt = Date.now();
    if (!this.shouldBlockForwardScroll(event.deltaY)) return;
    event.preventDefault();
    this.enforcePosterScrollLock();
  }

  shouldBlockForwardScroll(deltaY) {
    return !this.isIOS
      && !this.isHidden()
      && this.shouldLockOnCurrentEntry
      && this.isPosterAnimating
      && deltaY > 0;
  }

  updateEntryLockMode() {
    this.shouldLockOnCurrentEntry = !this.isIOS && Date.now() - this.lastUserScrollIntentAt < 240;
  }

  isHidden() {
    const wrapper = this.closest('.open-eyes-mobile-poster') || this;

    for (let element = wrapper; element && element.nodeType === Node.ELEMENT_NODE; element = element.parentElement) {
      if (window.getComputedStyle(element).display === 'none') return true;
    }

    return wrapper.getClientRects().length === 0;
  }

  onResize() {
    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      this.setupTimeline();
    }, 150);
  }

  setupTimeline() {
    if (!window.gsap) return;

    this.destroyTimeline(true);
    if (this.isHidden()) return;

    const screenWidth = window.innerWidth;
    const section = this.closest('.shopify-section');
    const root = this.closest('.open-eyes-mobile-poster');
    const nextSection = section ? section.nextElementSibling : null;
    const trigger = this.querySelector(`#open-eyes-${this.sectionId}`);
    const titleWrapper = this.querySelector('.title-wrapper');
    const playWrapper = this.querySelector('.player-wrapper');
    const contentWrapper = this.querySelector('.content-wrapper');
    const innerContent = this.querySelector('.player-inner-content');
    const player = this.querySelector('.player');
    const playerMask = this.querySelector('.player-mask');
    const openTextItems = Array.from(this.querySelectorAll('.open-eyes-wrapper .oetl'));
    const collapseTargets = [titleWrapper, contentWrapper].filter(Boolean);
    if (!trigger || !playWrapper || !innerContent || !playerMask) return;

    this.timeline = window.gsap.timeline({
      scrollTrigger: {
        trigger,
        pin: true,
        anticipatePin: 1,
        scrub: 1,
        start: 'top top',
        endTrigger: nextSection || trigger,
        end: 'top bottom',
        onEnter: () => this.updateEntryLockMode(),
        onEnterBack: () => this.updateEntryLockMode(),
        onUpdate: (self) => this.enforcePosterScrollLock(self),
        onLeave: (self) => this.enforcePosterScrollLock(self),
        onLeaveBack: () => this.stopPosterAnimation(root),
      },
    });

    this.timeline
      .to(openTextItems, {
        opacity: 1,
        x: `-=${0.5 * screenWidth}`,
        ease: 'power1.out',
        duration: 0.6,
      })
      .to(playWrapper, {
        opacity: 1,
        width: '100%',
        ease: 'power1.out',
        duration: 0.6,
      }, 0)
      .addLabel('text-complete')
      .to(playerMask, {
        backgroundPositionX: '400%',
        opacity: 1,
        duration: 0.4,
        onComplete: () => {
          if (player) player.classList.add('center');
          innerContent.style.zIndex = '2';
        },
        onReverseComplete: () => {
          if (player) player.classList.remove('center');
          innerContent.style.zIndex = '0';
        },
      })
      .to(playWrapper, {
        height: 40,
        duration: 0.2,
      })
      .to(innerContent, {
        opacity: 0,
        duration: 0.2,
        onComplete: () => {
          playWrapper.style.backgroundColor = 'rgba(0,0,0,0.4)';
        },
      }, '>0.2')
      .to(playWrapper, {
        height: '100vh',
        width: '100vw',
        borderRadius: 0,
        duration: 0.45,
        ease: 'power2.out',
        backgroundColor: 'rgba(0,0,0,0)',
        onStart: () => {
          playWrapper.classList.add('is-fullscreen');
        },
        onReverseComplete: () => {
          playWrapper.classList.remove('is-fullscreen');
          this.stopPosterAnimation(root);
        },
        onComplete: () => {
          this.startPosterAnimation(root);
          if (!this.isVideo) return;
          const video = this.querySelector('.player-wrapper video');
          if (video) video.play();
        },
      });

    this.timeline.fromTo(
        collapseTargets,
        {
          maxHeight(index, element) {
            element.style.overflow = 'hidden';
            return element.scrollHeight;
          },
          opacity: 1,
          marginTop: 0,
          marginBottom: 0,
          paddingTop: 0,
          paddingBottom: 0,
        },
        {
          maxHeight: 0,
          opacity: 0,
          marginTop: 0,
          marginBottom: 0,
          paddingTop: 0,
          paddingBottom: 0,
          duration: 0.12,
          ease: 'power2.in',
          onComplete() {
            collapseTargets.forEach((element) => {
              element.style.pointerEvents = 'none';
            });
          },
          onReverseComplete() {
            collapseTargets.forEach((element) => {
              element.style.pointerEvents = '';
              element.style.overflow = '';
              window.gsap.set(element, {
                clearProps: 'maxHeight,opacity,marginTop,marginBottom,paddingTop,paddingBottom',
              });
            });
          },
        },
        '>',
      )
      .addLabel('video')
      .to(playerMask, {
        duration: 0.4,
        opacity: 0.7,
        onComplete: () => {
          playerMask.style.opacity = '0';
        },
        onReverseComplete: () => {
          playerMask.style.opacity = '0';
        },
      }, 1)
      .to(this.querySelectorAll('.subtitle.oetl'), {
        duration: 0.2,
        fontWeight: 700,
        onReverseComplete: () => {
          if (!this.isVideo) return;
          const video = this.querySelector('.player-wrapper video');
          if (video) video.pause();
        },
      });

    this.timeline.to(playerMask, {
      duration: 0.1,
      opacity: 0,
    });

    this.textUnlockProgress = this.timeline.labels['text-complete'] / this.timeline.duration();
  }

  startPosterAnimation(root) {
    if (!root) return;

    root.classList.add('is-poster-ready');
    this.isPosterAnimating = true;
    this.lockPosterScroll();
    window.clearTimeout(this.posterAnimationTimer);
    this.posterAnimationTimer = window.setTimeout(() => {
      this.isPosterAnimating = false;
      this.posterAnimationTimer = null;
    }, 1260);
  }

  stopPosterAnimation(root) {
    if (root) root.classList.remove('is-poster-ready');
    this.isPosterAnimating = false;
    window.clearTimeout(this.posterAnimationTimer);
    this.posterAnimationTimer = null;
  }

  getScrollTrigger() {
    return this.timeline && this.timeline.scrollTrigger ? this.timeline.scrollTrigger : null;
  }

  lockPosterScroll() {
    if (this.isIOS || !this.shouldLockOnCurrentEntry) return;

    const scrollTrigger = this.getScrollTrigger();
    if (!scrollTrigger) return;

    this.lockedScrollY = Math.min(scrollTrigger.scroll(), scrollTrigger.end - 1);
    this.enforcePosterScrollLock(scrollTrigger);
  }

  enforcePosterScrollLock(scrollTrigger = this.getScrollTrigger()) {
    if (this.isIOS || !this.shouldLockOnCurrentEntry || !this.isPosterAnimating || !scrollTrigger) return;

    if (scrollTrigger.scroll() > this.lockedScrollY) {
      scrollTrigger.scroll(this.lockedScrollY);
    }
  }

  destroyTimeline(clearAnimatedProps = false) {
    if (this.timeline) {
      if (this.timeline.scrollTrigger) this.timeline.scrollTrigger.kill();
      this.timeline.kill();
      this.timeline = null;
    }

    this.textUnlockProgress = 0;

    if (!clearAnimatedProps || !window.gsap) return;

    const playWrapper = this.querySelector('.player-wrapper');
    const innerContent = this.querySelector('.player-inner-content');
    const player = this.querySelector('.player');
    const playerMask = this.querySelector('.player-mask');
    const animatedItems = Array.from(this.querySelectorAll('.open-eyes-wrapper .oetl'));
    const collapseTargets = Array.from(this.querySelectorAll('.title-wrapper, .content-wrapper'));
    const targets = [
      ...animatedItems,
      ...collapseTargets,
      playWrapper,
      innerContent,
      playerMask,
    ].filter(Boolean);

    if (player) player.classList.remove('center');
    if (playWrapper) playWrapper.classList.remove('is-fullscreen');
    const root = this.closest('.open-eyes-mobile-poster');
    this.stopPosterAnimation(root);

    window.gsap.set(targets, {
      clearProps:
        'opacity,visibility,autoAlpha,x,y,width,height,borderRadius,backgroundColor,backgroundPositionX,maxHeight,marginTop,marginBottom,paddingTop,paddingBottom,translateY,fontWeight',
    });

    collapseTargets.forEach((element) => {
      element.style.pointerEvents = '';
      element.style.overflow = '';
    });

    if (innerContent) innerContent.style.zIndex = '';
  }
}

if (!customElements.get('open-eyes-mobile-poster')) {
  customElements.define('open-eyes-mobile-poster', OpenEyesMobilePoster);
}
